import _ from 'lodash';
import { FetchResult, MutationResult } from '@apollo/client';
import React, { useState } from 'react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

// Material UI
import Typography from '@material-ui/core/Typography';

// Data
import { ICreateAssessmentSessionContainer, ICreateAssessmentSessionFields } from './create-assessment-session.interface';
import { ICreateSkillFields } from '../../Skill/CreateSkill/create-skill.interface';

// Components
import { CreateSession } from './create-assessment-session.component';
import { getActiveCompanyId, getCompany } from '../../../lib/services/company.service';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { getActiveAssessmentId, getAssessment } from '../../../lib/services/assessment.service';
import { CASCADING_SETTINGS } from '../../../lib/types/cascading-settings.types';
import { resolveCascadingSetting } from '../../../lib/helpers/cascading-settings.helpers';
import { DEFAULT_SESSION_DURATION_MINUTES } from '../session.constants';
import { SportTerminology } from '../../../lib/types';

export const CreateAssessmentSessionContainer: React.FC<ICreateAssessmentSessionContainer> = ({
  createAssessmentSession,
  createAgeGroup,
  ageGroups,
  ageGroupsLoading,
  ageGroupsError
}) => {
  const termLookup = useCompanySportTerminology();
  const facilityTerm = termLookup[SportTerminology.FACILITY]
  const sessionTerm = termLookup[SportTerminology.SESSION].toLowerCase()

  const [submitError, setSubmitError] = useState<string | null | undefined>(null);
  const [showSaved, setShowSaved] = useState(false);

  // Seeds the new ice time's RSVP toggle from the assessment, then association, default.
  // See "Cascading-setting defaults resolve on `null` only — never on `false`" in CLAUDE.md.
  const { company } = getCompany(getActiveCompanyId());
  const { assessment } = getAssessment(getActiveAssessmentId());

  const INITIAL_FORM_VALUES: ICreateAssessmentSessionFields = {
    location: '',
    address: '',
    date: dayjs().add(1, 'day').format('YYYY/MM/DD'),
    start: 60 * 12,
    duration: DEFAULT_SESSION_DURATION_MINUTES,
    isAttendanceRequested: resolveCascadingSetting(CASCADING_SETTINGS.IS_ATTENDANCE_REQUESTED, { assessment, company }),
  };

  async function handleSubmit(values: ICreateAssessmentSessionFields, { setSubmitting, resetForm }: FormikHelpers<ICreateAssessmentSessionFields>) {
    setSubmitError(null);

    createAssessmentSession({
      ..._.omit(values, ['ageGroupId', 'gender', 'duration']),
      duration: Number(values.duration),
      gender: values.gender?.value,
      ageGroupId: values.ageGroupId?.value
    })
      .then((result: MutationResult) => {
        setSubmitting(false);

        if (result.error) {
          setSubmitError(result.error.message);
        } else {
          setShowSaved(true);
          setTimeout(() => {
            setShowSaved(false);
          }, 2000);

          resetForm({ values });
        }
      });
  }

  const validationSchema = Yup.object().shape({
    location: Yup.string().required(`Enter the ${facilityTerm.toLowerCase()} name for the ${sessionTerm}`),
    address: Yup.string().required(`Enter the address of the ${sessionTerm}`),
    date: Yup.string().required(`Start date is required`),
    start: Yup.string().required('Start time is required'),
    duration: Yup.string().required('Duration is required'),
    ageGroupId: Yup.string().required('Select or create an age group'),
  });

  const ageGroupOptions = ageGroups.map((group: IAgeGroup) => ({ value: group._id, label: group.name }));
  const handleCreateAgeGroup = (form: FormikProps<ICreateSkillFields>, name: string) => {
    createAgeGroup(name).then((result: FetchResult) => {
      if (result.data && result.data.createAgeGroup) {
        form.setFieldValue('ageGroupId', { value: result.data.createAgeGroup._id, label: name });
        form.setFieldTouched('ageGroupId');
      }
    });
  }

  if (!ageGroups.length && !ageGroupsLoading && ageGroupsError) return (
    <Typography>Something went wrong. Please try again later</Typography>
  );

  return (
    <Formik
      enableReinitialize
      initialValues={INITIAL_FORM_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => <CreateSession
        form={form}
        submitError={submitError}
        showSaved={showSaved}
        createAgeGroup={handleCreateAgeGroup}
        ageGroupOptions={ageGroupOptions}
        ageGroupsLoading={ageGroupsLoading}
      />}
    </Formik>
  );
};

